import { useState } from "react";
import { useTranslation } from "react-i18next";
import DropdownAtom from "../../../components/atoms/dropdown/dropdown";
import ModalAtom from "../../../components/atoms/modal/modal";
import { API_URL } from "../../../constant/url";
import { showToast } from "../../../utils/method";

/** GAEB exchange phases as defined by AVACloud's DestinationGaebExchangePhase enum. */
const GAEB_PHASES = [
    { value: "Base", label: "lv.export-gaeb.phase-base" },
    { value: "CostEstimate", label: "lv.export-gaeb.phase-cost-estimate" },
    { value: "OfferRequest", label: "lv.export-gaeb.phase-offer-request" },
    { value: "Offer", label: "lv.export-gaeb.phase-offer" },
    { value: "SideOffer", label: "lv.export-gaeb.phase-side-offer" },
    { value: "Grant", label: "lv.export-gaeb.phase-grant" },
];

const GAEB_TYPES = [
    { value: "GaebXml_V3_2", label: "lv.export-gaeb.type-xml-v32" },
    { value: "GaebXml_V3_3", label: "lv.export-gaeb.type-xml-v33" },
    { value: "GaebXml_V3_1", label: "lv.export-gaeb.type-xml-v31" },
    { value: "Gaeb2000", label: "lv.export-gaeb.type-2000" },
    { value: "Gaeb90", label: "lv.export-gaeb.type-90" },
];

const ExportGaebModal = ({ show, onHide, lvData, projectId }) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState("OfferRequest");
    const [gaebType, setGaebType] = useState("GaebXml_V3_2");
    const [loading, setLoading] = useState(false);

    const handleExport = async () => {
        if (!lvData?.id) return;
        setLoading(true);
        try {
            const url = `${API_URL}projects/${projectId}/lvs/${lvData.id}/gaeb/export?phase=${encodeURIComponent(phase)}&gaebType=${encodeURIComponent(gaebType)}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                throw new Error(errBody?.message || t("errors.general"));
            }
            // Extract filename from Content-Disposition if present
            const disposition = res.headers.get("Content-Disposition") || "";
            const match = disposition.match(/filename="([^"]+)"/);
            const fileName = match ? match[1] : `${lvData.name || "export"}.x84`;

            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = fileName;
            anchor.click();
            URL.revokeObjectURL(objectUrl);
            onHide();
        } catch (err) {
            showToast(err.message || t("errors.general"), { type: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalAtom
            show={show}
            handleClose={onHide}
            handleSubmit={handleExport}
            title={t("lv.export-gaeb.title")}
            saveText={loading ? "common.loading" : "lv.export-gaeb.export-btn"}
            disabled={loading}
            body={
                <div className="d-flex flex-column gap-3">
                    <DropdownAtom
                        label={t("lv.export-gaeb.phase-label")}
                        value={phase}
                        options={GAEB_PHASES}
                        onChange={(e) => setPhase(e.target.value)}
                        shouldTranslate
                        sortOptions={false}
                    />
                    <small className="text-muted mt-n2">
                        {t("lv.export-gaeb.phase-hint")}
                    </small>

                    <DropdownAtom
                        label={t("lv.export-gaeb.type-label")}
                        value={gaebType}
                        options={GAEB_TYPES}
                        onChange={(e) => setGaebType(e.target.value)}
                        shouldTranslate
                        sortOptions={false}
                    />
                </div>
            }
        />
    );
};

export default ExportGaebModal;
